
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Heart, Star, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useLanguage } from '@/contexts/LanguageContext';

interface FavoriteRestaurant {
  id: string;
  created_at: string;
  restaurants: {
    id: string;
    name: string;
    description: string;
    image_url: string;
    rating: number;
    is_open: boolean;
  };
}

const FavoritesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [favorites, setFavorites] = useState<FavoriteRestaurant[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchFavorites();
    }
  }, [user]);

  const fetchFavorites = async () => {
    try {
      const { data, error } = await supabase
        .from('user_favorites')
        .select(`
          *,
          restaurants (
            id,
            name,
            description,
            image_url,
            rating,
            is_open
          )
        `)
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFavorites(data || []);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (favoriteId: string) => {
    try {
      const { error } = await supabase
        .from('user_favorites')
        .delete()
        .eq('id', favoriteId);

      if (error) throw error;
      
      setFavorites(favorites.filter(fav => fav.id !== favoriteId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-xl font-semibold">{t('favorites')}</h1>
        </div>
      </div>

      <div className="px-4 py-6 space-y-4">
        {favorites.length === 0 ? (
          <Card>
            <CardContent className="pt-16 pb-16 text-center">
              <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">{t('no_favorites_yet')}</p>
              <p className="text-sm text-gray-500 mb-6">
                {t('browse_restaurants_save')}
              </p>
              <Button 
                className="bg-red-600 hover:bg-red-700"
                onClick={() => navigate('/home')}
              >
                {t('explore_restaurants')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          favorites.map((favorite) => (
            <Card 
              key={favorite.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/restaurant/${favorite.restaurants.id}`)}
            >
              <CardContent className="p-0">
                <div className="relative">
                  <div className="h-32 bg-gray-200 rounded-t-lg overflow-hidden">
                    {favorite.restaurants.image_url ? (
                      <img 
                        src={favorite.restaurants.image_url} 
                        alt={favorite.restaurants.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-red-100 flex items-center justify-center">
                        <span className="text-red-600 font-bold text-2xl">
                          {favorite.restaurants.name.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 bg-white/80 hover:bg-white text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFavorite(favorite.id);
                    }}
                  >
                    <Heart className="h-5 w-5 fill-current" />
                  </Button>

                  {!favorite.restaurants.is_open && (
                    <div className="absolute inset-0 bg-black/50 rounded-t-lg flex items-center justify-center">
                      <span className="bg-white px-3 py-1 rounded-full text-sm font-medium">
                        {t('closed')}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {favorite.restaurants.name}
                      </h3>
                      {favorite.restaurants.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {favorite.restaurants.description}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <span className="font-medium">
                        {favorite.restaurants.rating?.toFixed(1) || '4.0'}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1 text-gray-500">
                      <Clock className="h-4 w-4" />
                      <span>20-30 {t('min')}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
